import React, { useState, useEffect } from 'react'
import {
  Card,
  Tag,
  Button,
  Space,
  Modal,
  Select,
  message,
  Input,
  Typography
} from 'antd'
import { SearchOutlined, EyeOutlined, SettingOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import api from '../../utils/api'
import dayjs from 'dayjs'
import AdminTable from '../../components/common/AdminTable'

const { Title, Text } = Typography
const { Option } = Select
const { TextArea } = Input

const PageContainer = styled.div`
  padding: 0;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`

// 莫兰迪色系配色
const colors = {
  cardBackground: '#faf9f6',
  border: 'rgba(168, 181, 192, 0.3)',
  shadow: 'rgba(154, 163, 175, 0.2)',
  textPrimary: '#5a5a5a',
  textSecondary: '#8a8a8a'
}

const StyledCard = styled(Card)`
  background: ${colors.cardBackground};
  border-radius: 16px;
  box-shadow: 0 4px 16px ${colors.shadow};
  border: 1px solid ${colors.border};
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;

  .ant-card-body {
    padding: 24px;
    width: 100%;
    box-sizing: border-box;
    overflow-x: hidden;

    @media (max-width: 768px) {
      padding: 16px 12px;
    }
  }

  @media (max-width: 768px) {
    border-radius: 12px;
  }
`

const FilterSection = styled.div`
  margin-bottom: 24px;
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`

const StatusTag = ({ status }) => {
  const config = {
    pending: { color: 'gold', text: '待審核' },
    approved: { color: 'green', text: '已批准' },
    rejected: { color: 'red', text: '已拒絕' }
  }
  const { color, text } = config[status] || { color: 'default', text: status }
  return <Tag color={color}>{text}</Tag>
}

const PaymentApplicationsPage = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState([])
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  })
  const [filters, setFilters] = useState({
    status: undefined
  })

  const fetchData = async (page = 1, pageSize = 10) => {
    setLoading(true)
    try {
      const params = {
        page,
        limit: pageSize,
        ...filters
      }
      // 清除未定义的参数
      Object.keys(params).forEach(
        (key) => params[key] === undefined && delete params[key]
      )

      const response = await api.get('/api/admin/payment-applications', {
        params
      })
      if (response.data.success) {
        setData(response.data.data)
        setPagination({
          current: response.data.pagination.currentPage,
          pageSize: response.data.pagination.itemsPerPage,
          total: response.data.pagination.totalItems
        })
      }
    } catch (error) {
      console.error('獲取支付申請列表失敗:', error)
      message.error('獲取列表失敗')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData(pagination.current, pagination.pageSize)
  }, [filters]) // pagination changes handled by Table onChange

  const handleTableChange = (newPagination) => {
    fetchData(newPagination.current, newPagination.pageSize)
  }

  const handleViewDetail = (record) => {
    navigate(`/admin/payment-applications/${record._id}`)
  }

  const columns = [
    {
      title: '申請編號',
      dataIndex: 'applicationId',
      key: 'applicationId',
      render: (text) => <Text copyable>{text}</Text>
    },
    {
      title: '店家名稱',
      dataIndex: 'businessName',
      key: 'businessName'
    },
    {
      title: '申請人',
      dataIndex: 'memberName',
      key: 'memberName'
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <StatusTag status={status} />
    },
    {
      title: '提交時間',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm')
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size='middle'>
          <Button
            type='link'
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          >
            查看詳情
          </Button>
          {record.status === 'approved' && (
            <Button
              type='link'
              icon={<SettingOutlined />}
              onClick={() => handleViewDetail(record)}
            >
              配置金鑰
            </Button>
          )}
        </Space>
      )
    }
  ]

  return (
    <PageContainer>
      <Header>
        <Title level={3} style={{ margin: 0 }}>
          支付申請管理
        </Title>
        <Button
          icon={<SearchOutlined />}
          onClick={() => fetchData(pagination.current, pagination.pageSize)}
        >
          刷新
        </Button>
      </Header>

      <StyledCard>
        <FilterSection>
          <span style={{ fontWeight: 500 }}>狀態篩選：</span>
          <Select
            placeholder='全部狀態'
            style={{ width: 200 }}
            allowClear
            onChange={(val) => setFilters({ ...filters, status: val })}
          >
            <Option value='pending'>待審核</Option>
            <Option value='approved'>已批准</Option>
            <Option value='rejected'>已拒絕</Option>
          </Select>
        </FilterSection>

        <AdminTable
          columns={columns}
          dataSource={data}
          rowKey='_id'
          pagination={{
            currentPage: pagination.current,
            totalItems: pagination.total,
            itemsPerPage: pagination.pageSize
          }}
          loading={loading}
          onChange={handleTableChange}
        />
      </StyledCard>
    </PageContainer>
  )
}

export default PaymentApplicationsPage
